// File: frontend/src/components/ReportKnowledgeGraph.tsx
// "Knowledge Graph" tab on the report detail page: the report's canonical
// entities and their observed relationships, with corpus-wide context
// (node size = how many reports mention the entity). Click a node to open
// its entity profile.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import {
    Box,
    Paper,
    Typography,
    Chip,
    Stack,
    Alert,
    CircularProgress,
    FormControlLabel,
    Switch,
    alpha,
} from '@mui/material';
import { useApi } from '../hooks/useApi';
import { cyber } from '../theme';
import { ENTITY_TYPE_COLORS } from '../pages/EntityProfilePage';

interface ReportGraphNode {
    id: string;
    name: string;
    type: string;
    report_count: number;
}

interface ReportGraphEdge {
    source: string;
    target: string;
    label: string | null;
}

interface ReportGraphResponse {
    node_count: number;
    edge_count: number;
    nodes: ReportGraphNode[];
    edges: ReportGraphEdge[];
}

const ReportKnowledgeGraph: React.FC<{ reportId: string }> = ({ reportId }) => {
    const { authenticatedFetcher } = useApi();
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const cyRef = useRef<any>(null);
    const [includeIndicators, setIncludeIndicators] = useState(false);
    const [renderError, setRenderError] = useState<string | null>(null);

    const { data, error, isLoading } = useSWR<ReportGraphResponse>(
        reportId ? `/api/kg/report/${reportId}/graph?include_indicators=${includeIndicators}` : null,
        authenticatedFetcher,
        { revalidateOnFocus: false, shouldRetryOnError: false }
    );

    useEffect(() => {
        if (!data || !containerRef.current) return;
        let cancelled = false;

        (async () => {
            try {
                const [{ default: cytoscape }, { default: coseBilkent }] = await Promise.all([
                    import('cytoscape'),
                    import('cytoscape-cose-bilkent'),
                ]);
                try { cytoscape.use(coseBilkent); } catch { /* already registered */ }
                if (cancelled || !containerRef.current) return;

                cyRef.current?.destroy();

                const maxCount = Math.max(...data.nodes.map((n) => n.report_count), 1);
                const cy = cytoscape({
                    container: containerRef.current,
                    elements: [
                        ...data.nodes.map((n) => ({
                            data: {
                                id: n.id,
                                label: n.name.length > 28 ? `${n.name.slice(0, 28)}…` : n.name,
                                type: n.type,
                                reportCount: n.report_count,
                                // 22px for first-seen entities up to 55px for corpus-wide hubs
                                size: 22 + Math.round(33 * Math.log(n.report_count) / Math.log(Math.max(maxCount, 2))),
                            },
                        })),
                        ...data.edges.map((e, i) => ({
                            data: { id: `e${i}`, source: e.source, target: e.target, label: e.label || '' },
                        })),
                    ],
                    style: [
                        {
                            selector: 'node',
                            style: {
                                'background-color': (el: any) => ENTITY_TYPE_COLORS[el.data('type')] || '#9fa4c0',
                                label: 'data(label)',
                                color: '#e8e8f0',
                                'font-size': '9px',
                                'text-valign': 'bottom',
                                'text-margin-y': 4,
                                width: 'data(size)',
                                height: 'data(size)',
                                'border-width': 1.5,
                                'border-color': (el: any) => alpha(ENTITY_TYPE_COLORS[el.data('type')] || '#9fa4c0', 0.5),
                            },
                        },
                        {
                            selector: 'edge',
                            style: {
                                width: 1,
                                'line-color': alpha('#9fa4c0', 0.35),
                                'curve-style': 'bezier',
                            },
                        },
                    ],
                    layout: { name: 'cose-bilkent', animate: false, nodeDimensionsIncludeLabels: true } as any,
                    wheelSensitivity: 0.2,
                });

                cy.on('tap', 'node', (evt: any) => navigate(`/entity/${evt.target.id()}`));
                cyRef.current = cy;
                setRenderError(null);
            } catch (e: any) {
                setRenderError(e?.message || 'Graph rendering failed');
            }
        })();

        return () => {
            cancelled = true;
            cyRef.current?.destroy();
            cyRef.current = null;
        };
    }, [data, navigate]);

    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}><CircularProgress /></Box>;
    }
    if (error) {
        return (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
                This report is not in the Knowledge Graph yet (new reports are ingested every 15 minutes),
                or the graph is currently unavailable.
            </Alert>
        );
    }
    if (!data) return null;

    const typesPresent = Array.from(new Set(data.nodes.map((n) => n.type)));
    const recurring = data.nodes.filter((n) => n.report_count > 1).length;

    return (
        <Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        {data.node_count} canonical entities, {data.edge_count} observed relationships.
                        Node size = corpus-wide presence: <strong>{recurring}</strong> of these entities
                        appear in other reports too. Click a node to open its profile.
                    </Typography>
                </Box>
                <FormControlLabel
                    control={<Switch size="small" checked={includeIndicators} onChange={(e) => setIncludeIndicators(e.target.checked)} />}
                    label={<Typography variant="caption">Include indicators</Typography>}
                />
            </Stack>

            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                {typesPresent.map((t) => (
                    <Chip
                        key={t}
                        label={t}
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            color: ENTITY_TYPE_COLORS[t] || '#9fa4c0',
                            bgcolor: alpha(ENTITY_TYPE_COLORS[t] || '#9fa4c0', 0.12),
                            border: `1px solid ${alpha(ENTITY_TYPE_COLORS[t] || '#9fa4c0', 0.3)}`,
                        }}
                    />
                ))}
            </Stack>

            {renderError && <Alert severity="error" sx={{ mb: 1 }}>{renderError}</Alert>}

            <Paper
                ref={containerRef}
                sx={{
                    height: 520,
                    borderRadius: 2,
                    border: `1px solid ${alpha(cyber.primary, 0.2)}`,
                    bgcolor: 'background.default',
                }}
            />
        </Box>
    );
};

export default ReportKnowledgeGraph;
